/**
 * ============================================================
 * System       : CSAF Alert Finance
 * File         : frontend/src/pages/ReconciliationHistory.jsx
 * Description  : Browse previously-saved SAP vs Bank reconciliation runs
 *                (see pages/Reconciliation.jsx for the upload/compare tool
 *                that produces them) by period, and view any one in full.
 * ============================================================
 */

import React, { useState, useEffect, useCallback, useMemo } from "react";
import { FileClock, ArrowLeft, Trash2, CheckCircle2, ScanLine, Download, Save, Loader2 } from "lucide-react";
import { getReconciliations, getReconciliation, updateReconciliation, deleteReconciliation } from "../api";
import { useToast } from "../App";
import {
  StatChip, DataTable, ManualMatchPanel, ManualMatchTray, SuggestionCard, suggestManualMatches,
  thb, round2, labelToMD, isoToMD, inDateRange, exportWorkbook,
} from "./Reconciliation";

// Same tab set/columns as the live tool (pages/Reconciliation.jsx) so a
// saved report looks identical to when it was first produced. "manual" has
// no countKey from the runs list row (older saved runs predate the feature
// and have no payload.manualMatched at all) — its count is read straight
// off the detail payload instead, see the tab-count lookup below.
const TABS = [
  { key: "matched", label: "จับคู่ได้", tone: "green", countKey: "matched_count" },
  { key: "sapUnmatched", label: "SAP หาคู่ไม่เจอ", tone: "red", countKey: "sap_unmatched_count" },
  { key: "bankUnmatched", label: "Bank หาคู่ไม่เจอ", tone: "red", countKey: "bank_unmatched_count" },
  { key: "manual", label: "จับคู่ยอดต่าง", tone: "purple", countKey: "__manualCount" },
  { key: "dateGap", label: "วันที่ไม่ตรงกัน", tone: "amber", countKey: "date_gap_count" },
  { key: "daily", label: "สรุปรายวัน", tone: "navy", countKey: "__dailyCount" },
];

const TAB_COLUMNS = {
  matched: [
    { key: "dateSap", label: "วันที่ SAP", date: true },
    { key: "dateBank", label: "วันที่ Bank", date: true },
    { key: "amount", label: "จำนวนเงิน", numeric: true },
    { key: "docno", label: "เลขที่เอกสาร" },
    { key: "textSap", label: "รายละเอียด SAP" },
    { key: "detailBank", label: "รายละเอียด Bank" },
  ],
  sapUnmatched: [
    { key: "date", label: "วันที่", date: true },
    { key: "amount", label: "จำนวนเงิน", numeric: true },
    { key: "docno", label: "เลขที่เอกสาร" },
    { key: "text", label: "รายละเอียด" },
  ],
  bankUnmatched: [
    { key: "date", label: "วันที่", date: true },
    { key: "amount", label: "จำนวนเงิน", numeric: true },
    { key: "channel", label: "ช่องทาง" },
    { key: "detail", label: "รายละเอียด" },
  ],
  dateGap: [
    { key: "diff", label: "ส่วนต่าง (วัน)", numeric: true },
    { key: "dateSap", label: "วันที่ SAP", date: true },
    { key: "dateBank", label: "วันที่ Bank", date: true },
    { key: "amount", label: "จำนวนเงิน", numeric: true },
    { key: "textSap", label: "รายละเอียด SAP" },
  ],
  daily: [
    { key: "date", label: "วันที่", date: true },
    { key: "sapTotal", label: "ยอด SAP", numeric: true },
    { key: "sapCount", label: "จำนวนรายการ SAP", numeric: true, count: true },
    { key: "bankTotal", label: "ยอด Bank", numeric: true },
    { key: "bankCount", label: "จำนวนรายการ Bank", numeric: true, count: true },
    { key: "diff", label: "ส่วนต่าง", numeric: true },
  ],
};

// Rebuilds the same per-day breakdown the live tool computes from raw rows —
// but from a SAVED run's already-flattened payload, since that's all a
// saved report keeps. `matched` rows contribute to both sides (their
// dateSap/dateBank can differ); dateGap is a subset of matched, so it must
// not be counted again here.
function buildDailySummaryFromPayload(payload) {
  const map = new Map();
  const bucket = (label) => {
    if (!map.has(label)) map.set(label, { date: label, sapTotal: 0, sapCount: 0, bankTotal: 0, bankCount: 0 });
    return map.get(label);
  };
  (payload.matched || []).forEach((r) => {
    const s = bucket(r.dateSap);
    s.sapTotal = round2(s.sapTotal + r.amount);
    s.sapCount += 1;
    const b = bucket(r.dateBank);
    b.bankTotal = round2(b.bankTotal + r.amount);
    b.bankCount += 1;
  });
  (payload.sapUnmatched || []).forEach((r) => {
    const e = bucket(r.date);
    e.sapTotal = round2(e.sapTotal + r.amount);
    e.sapCount += 1;
  });
  (payload.bankUnmatched || []).forEach((r) => {
    const e = bucket(r.date);
    e.bankTotal = round2(e.bankTotal + r.amount);
    e.bankCount += 1;
  });
  return Array.from(map.values())
    .map((e) => ({ ...e, diff: round2(e.sapTotal - e.bankTotal) }))
    .sort((a, b) => labelToMD(a.date) - labelToMD(b.date));
}

function fmtDateTime(iso) {
  if (!iso) return "-";
  const d = new Date(iso);
  return d.toLocaleString("th-TH", { year: "numeric", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
}

export default function ReconciliationHistory() {
  const { addToast } = useToast();
  const [runs, setRuns] = useState(null);
  const [query, setQuery] = useState("");
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [tab, setTab] = useState("matched");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  // Editable manual matching — same shape/flow as the live tool
  // (Reconciliation.jsx), just seeded from a saved report's payload
  // instead of a fresh comparison. `dirty` tracks whether anything here
  // differs from what's actually saved in the DB (see handleSaveMatches).
  const [manualMatches, setManualMatches] = useState([]);
  const [selectedSap, setSelectedSap] = useState(() => new Set());
  const [selectedBank, setSelectedBank] = useState(() => new Set());
  const [suggestions, setSuggestions] = useState(null);
  const [suggesting, setSuggesting] = useState(false);
  const [dismissedSuggestions, setDismissedSuggestions] = useState(() => new Set());
  const [dirty, setDirty] = useState(false);
  const [savingMatch, setSavingMatch] = useState(false);

  const loadList = useCallback(async () => {
    try {
      const res = await getReconciliations();
      setRuns(res.data);
    } catch (e) {
      addToast("error", e.response?.data?.detail || "โหลดรายการรายงานไม่สำเร็จ");
      setRuns([]);
    }
  }, [addToast]);

  useEffect(() => { loadList(); }, [loadList]);

  const openDetail = async (id) => {
    setSelectedId(id);
    setDetail(null);
    setDetailLoading(true);
    setTab("matched");
    setDateFrom("");
    setDateTo("");
    setManualMatches([]);
    setSelectedSap(new Set());
    setSelectedBank(new Set());
    setSuggestions(null);
    setDismissedSuggestions(new Set());
    setDirty(false);
    try {
      const res = await getReconciliation(id);
      setDetail(res.data);
      setManualMatches(res.data.payload.manualMatched || []);
    } catch (e) {
      addToast("error", e.response?.data?.detail || "โหลดรายงานไม่สำเร็จ");
      setSelectedId(null);
    } finally {
      setDetailLoading(false);
    }
  };

  const handleDelete = async (id, e) => {
    e.stopPropagation();
    if (!window.confirm("ต้องการลบรายงานนี้หรือไม่?")) return;
    try {
      await deleteReconciliation(id);
      addToast("success", "ลบรายงานสำเร็จ");
      if (selectedId === id) { setSelectedId(null); setDetail(null); }
      loadList();
    } catch (e2) {
      addToast("error", e2.response?.data?.detail || "ลบรายงานไม่สำเร็จ");
    }
  };

  const filteredRuns = (runs || []).filter((r) =>
    !query.trim() || r.period.toLowerCase().includes(query.trim().toLowerCase())
  );

  // Daily summary intentionally stays sourced from the ORIGINAL saved
  // payload, not editablePayload below — same as the live tool's own
  // per-day totals, it counts every parsed row regardless of match status,
  // so pulling a row into a manual match group shouldn't make its amount
  // vanish from "สรุปรายวัน".
  const dailySummary = useMemo(
    () => (detail ? buildDailySummaryFromPayload(detail.payload) : []),
    [detail]
  );

  // Only reports saved after this feature shipped have sapIdx/bankIdx on
  // their unmatched rows — older ones can still be viewed, just not
  // manually matched (there's no stable identity to select by).
  const supportsManualMatch = !detail
    ? false
    : (detail.payload.sapUnmatched || []).every((r) => r.sapIdx !== undefined) &&
      (detail.payload.bankUnmatched || []).every((r) => r.bankIdx !== undefined);

  const matchedSapIdx = useMemo(() => new Set(manualMatches.flatMap((g) => g.sapRows.map((r) => r.sapIdx))), [manualMatches]);
  const matchedBankIdx = useMemo(() => new Set(manualMatches.flatMap((g) => g.bankRows.map((r) => r.bankIdx))), [manualMatches]);

  // Mirrors Reconciliation.jsx's `payload` memo — the saved sapUnmatched/
  // bankUnmatched with anything already folded into a manual match group
  // pulled back out, so every tab/count downstream reflects "what's
  // actually still unmatched right now", not what was true at save time.
  const editablePayload = useMemo(() => {
    if (!detail) return null;
    const p = detail.payload;
    return {
      ...p,
      sapUnmatched: (p.sapUnmatched || []).filter((r) => !matchedSapIdx.has(r.sapIdx)),
      bankUnmatched: (p.bankUnmatched || []).filter((r) => !matchedBankIdx.has(r.bankIdx)),
      manualMatched: manualMatches,
    };
  }, [detail, matchedSapIdx, matchedBankIdx, manualMatches]);

  const fromMD = isoToMD(dateFrom);
  const toMD = isoToMD(dateTo);
  const hasDateFilter = fromMD != null || toMD != null;
  // Same convention as the live tool: the summary card above always shows
  // the full saved totals — the date filter only narrows the tabs below.
  const filteredPayload = useMemo(() => {
    if (!editablePayload) return null;
    const p = editablePayload;
    if (!hasDateFilter) return p;
    return {
      matched: (p.matched || []).filter((r) => inDateRange(r.dateSap, fromMD, toMD)),
      sapUnmatched: (p.sapUnmatched || []).filter((r) => inDateRange(r.date, fromMD, toMD)),
      bankUnmatched: (p.bankUnmatched || []).filter((r) => inDateRange(r.date, fromMD, toMD)),
      dateGap: (p.dateGap || []).filter((r) => inDateRange(r.dateSap, fromMD, toMD)),
    };
  }, [editablePayload, hasDateFilter, fromMD, toMD]);
  const filteredDailySummary = useMemo(() => {
    if (!hasDateFilter) return dailySummary;
    return dailySummary.filter((r) => inDateRange(r.date, fromMD, toMD));
  }, [dailySummary, hasDateFilter, fromMD, toMD]);

  const toggleSapSelect = (idx) => setSelectedSap((prev) => {
    const next = new Set(prev);
    next.has(idx) ? next.delete(idx) : next.add(idx);
    return next;
  });
  const toggleBankSelect = (idx) => setSelectedBank((prev) => {
    const next = new Set(prev);
    next.has(idx) ? next.delete(idx) : next.add(idx);
    return next;
  });
  const toggleAllSap = (ids, checked) => setSelectedSap((prev) => {
    const next = new Set(prev);
    ids.forEach((id) => (checked ? next.add(id) : next.delete(id)));
    return next;
  });
  const toggleAllBank = (ids, checked) => setSelectedBank((prev) => {
    const next = new Set(prev);
    ids.forEach((id) => (checked ? next.add(id) : next.delete(id)));
    return next;
  });

  const selectedSapRows = editablePayload ? editablePayload.sapUnmatched.filter((r) => selectedSap.has(r.sapIdx)) : [];
  const selectedBankRows = editablePayload ? editablePayload.bankUnmatched.filter((r) => selectedBank.has(r.bankIdx)) : [];

  const confirmManualMatch = () => {
    if (!selectedSapRows.length || !selectedBankRows.length) return;
    const sapTotal = round2(selectedSapRows.reduce((s, r) => s + r.amount, 0));
    const bankTotal = round2(selectedBankRows.reduce((s, r) => s + r.amount, 0));
    setManualMatches((prev) => [...prev, {
      id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      sapRows: selectedSapRows, bankRows: selectedBankRows, sapTotal, bankTotal, diff: round2(sapTotal - bankTotal),
    }]);
    setSelectedSap(new Set());
    setSelectedBank(new Set());
    setDirty(true);
  };
  const clearSelection = () => { setSelectedSap(new Set()); setSelectedBank(new Set()); };
  const undoManualMatch = (id) => { setManualMatches((prev) => prev.filter((g) => g.id !== id)); setDirty(true); };

  const runSuggestions = () => {
    if (!editablePayload) return;
    setSuggesting(true);
    setTimeout(() => {
      setSuggestions(suggestManualMatches(editablePayload.sapUnmatched, editablePayload.bankUnmatched));
      setDismissedSuggestions(new Set());
      setSuggesting(false);
    }, 30);
  };
  const visibleSuggestions = useMemo(() => {
    if (!suggestions) return [];
    return suggestions.filter((s) =>
      !dismissedSuggestions.has(s.id) &&
      !s.sapRows.some((r) => matchedSapIdx.has(r.sapIdx)) &&
      !s.bankRows.some((r) => matchedBankIdx.has(r.bankIdx))
    );
  }, [suggestions, dismissedSuggestions, matchedSapIdx, matchedBankIdx]);
  const acceptSuggestion = (s) => {
    setManualMatches((prev) => [...prev, {
      id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      sapRows: s.sapRows, bankRows: s.bankRows, sapTotal: s.sapTotal, bankTotal: s.bankTotal, diff: s.diff,
    }]);
    setDirty(true);
  };
  const dismissSuggestion = (id) => setDismissedSuggestions((prev) => new Set(prev).add(id));

  // Persists the manual-matching changes back to this saved run — only the
  // fields that manual matching can actually change (see the PUT /:id
  // route). Updates `detail`/`runs` in place afterward so the summary
  // chips, tab counts, and the list view's "ต้องตรวจสอบ" column all reflect
  // the new state without a full reload.
  const handleSaveMatches = async () => {
    if (!detail || !editablePayload) return;
    setSavingMatch(true);
    try {
      const sap_unmatched_count = editablePayload.sapUnmatched.length;
      const bank_unmatched_count = editablePayload.bankUnmatched.length;
      await updateReconciliation(detail.id, { sap_unmatched_count, bank_unmatched_count, payload: editablePayload });
      setDetail((d) => ({ ...d, sap_unmatched_count, bank_unmatched_count, payload: editablePayload }));
      setRuns((prev) => (prev || []).map((r) => (r.id === detail.id ? { ...r, sap_unmatched_count, bank_unmatched_count } : r)));
      setDirty(false);
      addToast("success", "บันทึกการจับคู่เพิ่มเติมสำเร็จ");
    } catch (e) {
      addToast("error", e.response?.data?.detail || "บันทึกไม่สำเร็จ");
    } finally {
      setSavingMatch(false);
    }
  };

  return (
    <div
      style={{
        fontFamily: "'Inter', 'Noto Sans Thai', system-ui, sans-serif",
        background: "#F6F4EE",
        minHeight: "100%",
        padding: "28px 20px 60px",
        color: "#2A3441",
      }}
    >
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      <div style={{ maxWidth: 960, margin: "0 auto" }}>
        {/* Header */}
        <div style={{ marginBottom: 26 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
            <ScanLine size={18} color="#1F6650" />
            <span style={{ fontSize: 11.5, letterSpacing: "0.08em", color: "#1F6650", fontWeight: 700, textTransform: "uppercase" }}>
              Reconciliation Tool
            </span>
          </div>
          <h1 style={{ fontFamily: "'Fraunces', serif", fontSize: 30, fontWeight: 600, color: "#132A3A", margin: 0, lineHeight: 1.25 }}>
            รายงานตรวจสอบยอด SAP & Bank
          </h1>
          <p style={{ fontSize: 13.5, color: "#63697A", marginTop: 6, maxWidth: 560 }}>
            รายงานที่บันทึกไว้จากหน้า "ตรวจสอบยอด" แยกตามช่วงเวลา/เดือน
          </p>
        </div>

        {!selectedId ? (
          <div style={{ background: "#fff", borderRadius: 16, border: "1px solid #E7E3D8", padding: 20, boxShadow: "0 1px 2px rgba(19,42,58,0.04)" }}>
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="ค้นหาช่วงเวลา/เดือน..."
              style={{
                width: "100%",
                maxWidth: 320,
                fontSize: 13,
                padding: "9px 12px",
                borderRadius: 8,
                border: "1px solid #DADFE6",
                outline: "none",
                fontFamily: "inherit",
                marginBottom: 14,
              }}
            />
            {runs === null ? (
              <div style={{ padding: 40, textAlign: "center", color: "#8B93A1" }}>กำลังโหลด...</div>
            ) : filteredRuns.length === 0 ? (
              <div style={{ padding: 40, textAlign: "center", color: "#8B93A1" }}>
                <FileClock size={26} style={{ marginBottom: 8 }} />
                <div>ยังไม่มีรายงานที่บันทึกไว้</div>
              </div>
            ) : (
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12.5 }}>
                  <thead>
                    <tr style={{ borderBottom: "1px solid #E7EAEF" }}>
                      <th style={{ textAlign: "left", padding: "8px 10px", color: "#8B93A1", fontWeight: 600, fontSize: 11.5 }}>ช่วงเวลา</th>
                      <th style={{ textAlign: "right", padding: "8px 10px", color: "#8B93A1", fontWeight: 600, fontSize: 11.5 }}>รวม SAP</th>
                      <th style={{ textAlign: "right", padding: "8px 10px", color: "#8B93A1", fontWeight: 600, fontSize: 11.5 }}>รวม Bank</th>
                      <th style={{ textAlign: "center", padding: "8px 10px", color: "#8B93A1", fontWeight: 600, fontSize: 11.5 }}>จับคู่ได้</th>
                      <th style={{ textAlign: "center", padding: "8px 10px", color: "#8B93A1", fontWeight: 600, fontSize: 11.5 }}>ต้องตรวจสอบ</th>
                      <th style={{ textAlign: "left", padding: "8px 10px", color: "#8B93A1", fontWeight: 600, fontSize: 11.5 }}>บันทึกเมื่อ</th>
                      <th style={{ width: 40 }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredRuns.map((r) => (
                      <tr
                        key={r.id}
                        onClick={() => openDetail(r.id)}
                        style={{ borderBottom: "1px solid #F1F2F4", cursor: "pointer" }}
                        onMouseEnter={(e) => (e.currentTarget.style.background = "#FAFBFC")}
                        onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                      >
                        <td style={{ padding: "10px", fontWeight: 600, color: "#132A3A" }}>{r.period}</td>
                        <td style={{ padding: "10px", textAlign: "right", fontFamily: "'IBM Plex Mono', monospace" }}>{thb(parseFloat(r.sap_total))}</td>
                        <td style={{ padding: "10px", textAlign: "right", fontFamily: "'IBM Plex Mono', monospace" }}>{thb(parseFloat(r.bank_total))}</td>
                        <td style={{ padding: "10px", textAlign: "center", color: "#1F6650", fontWeight: 600 }}>{r.matched_count}</td>
                        <td style={{ padding: "10px", textAlign: "center", color: "#B3261E", fontWeight: 600 }}>
                          {r.sap_unmatched_count + r.bank_unmatched_count}
                        </td>
                        <td style={{ padding: "10px", color: "#63697A" }}>
                          {fmtDateTime(r.created_at)}{r.fname ? ` · ${r.fname} ${r.lname || ""}` : ""}
                        </td>
                        <td style={{ padding: "10px", textAlign: "center" }}>
                          <button
                            onClick={(e) => handleDelete(r.id, e)}
                            title="ลบรายงาน"
                            style={{ border: "none", background: "transparent", color: "#B3261E", cursor: "pointer", padding: 4 }}
                          >
                            <Trash2 size={15} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        ) : (
          <div>
            <button
              onClick={() => { setSelectedId(null); setDetail(null); }}
              style={{
                display: "flex", alignItems: "center", gap: 6, marginBottom: 16,
                border: "none", background: "transparent", color: "#63697A", fontSize: 13, cursor: "pointer", padding: 0,
              }}
            >
              <ArrowLeft size={15} /> กลับไปรายการรายงาน
            </button>

            {detailLoading || !detail ? (
              <div style={{ padding: 40, textAlign: "center", color: "#8B93A1" }}>กำลังโหลด...</div>
            ) : (
              <>
                <div style={{ background: "#fff", borderRadius: 16, border: "1px solid #E7E3D8", padding: "24px 26px 20px", boxShadow: "0 1px 2px rgba(19,42,58,0.04)" }}>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 18, flexWrap: "wrap", gap: 10 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <CheckCircle2 size={18} color="#1F6650" />
                      <span style={{ fontFamily: "'Fraunces', serif", fontSize: 18, fontWeight: 600, color: "#132A3A" }}>
                        {detail.period}
                      </span>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap" }}>
                      <span style={{ fontSize: 12, color: "#8B93A1" }}>
                        บันทึกเมื่อ {fmtDateTime(detail.created_at)}
                        {detail.fname ? ` โดย ${detail.fname} ${detail.lname || ""}` : ""}
                      </span>
                      {dirty && (
                        <button
                          onClick={handleSaveMatches}
                          disabled={savingMatch}
                          style={{
                            display: "flex", alignItems: "center", gap: 6, padding: "8px 14px", borderRadius: 8, border: "none",
                            background: "#132A3A", color: "#fff", fontSize: 12.5, fontWeight: 600,
                            cursor: savingMatch ? "default" : "pointer", opacity: savingMatch ? 0.7 : 1, whiteSpace: "nowrap",
                          }}
                        >
                          {savingMatch ? (
                            <><Loader2 size={14} style={{ animation: "spin 1s linear infinite" }} /> กำลังบันทึก...</>
                          ) : (
                            <><Save size={14} /> บันทึกการเปลี่ยนแปลง</>
                          )}
                        </button>
                      )}
                      <button
                        onClick={() => exportWorkbook({
                          payload: editablePayload,
                          dailySummary,
                          sapCount: detail.sap_count,
                          bankCount: detail.bank_count,
                          sapTotal: parseFloat(detail.sap_total),
                          bankTotal: parseFloat(detail.bank_total),
                          filename: `CSA_Reconciliation_${detail.period}.xlsx`,
                        })}
                        style={{
                          display: "flex", alignItems: "center", gap: 6, padding: "8px 14px", borderRadius: 8,
                          border: "1px solid #1F6650", background: "#E7F3EC", color: "#1F6650",
                          fontSize: 12.5, fontWeight: 600, cursor: "pointer", whiteSpace: "nowrap",
                        }}
                      >
                        <Download size={14} /> ดาวน์โหลด Excel
                      </button>
                    </div>
                  </div>

                  <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 16 }}>
                    <StatChip value={detail.sap_count.toLocaleString()} label="รายการ SAP ทั้งหมด" tone="navy" />
                    <StatChip value={detail.bank_count.toLocaleString()} label="รายการ Bank ทั้งหมด" tone="navy" />
                    <StatChip value={detail.matched_count.toLocaleString()} label="จับคู่ได้ตรงกัน" tone="green" />
                    <StatChip
                      value={(editablePayload.sapUnmatched.length + editablePayload.bankUnmatched.length).toLocaleString()}
                      label="รายการต้องตรวจสอบ"
                      tone="red"
                    />
                    {manualMatches.length > 0 && (
                      <StatChip value={manualMatches.length.toLocaleString()} label="จับคู่ยอดต่างแล้ว" tone="purple" />
                    )}
                  </div>
                  {dirty && (
                    <div style={{ fontSize: 11.5, color: "#8A5A00", marginBottom: 4 }}>
                      มีการจับคู่ที่ยังไม่ได้บันทึก — กด "บันทึกการเปลี่ยนแปลง" ด้านบนเพื่อบันทึกลงรายงาน
                    </div>
                  )}

                  <div style={{ borderTop: "1.5px dashed #DADFE6", paddingTop: 12, display: "flex", justifyContent: "space-between", fontSize: 12.5, color: "#63697A", flexWrap: "wrap", gap: 8 }}>
                    <span>ผลรวม SAP: <b style={{ fontFamily: "'IBM Plex Mono', monospace", color: "#132A3A" }}>฿{thb(parseFloat(detail.sap_total))}</b></span>
                    <span>ผลรวม Bank: <b style={{ fontFamily: "'IBM Plex Mono', monospace", color: "#132A3A" }}>฿{thb(parseFloat(detail.bank_total))}</b></span>
                    <span>ส่วนต่าง: <b style={{ fontFamily: "'IBM Plex Mono', monospace", color: "#B3261E" }}>฿{thb(round2(Math.abs(parseFloat(detail.diff_total))))}</b></span>
                  </div>
                  {(detail.sap_filename || detail.bank_filename) && (
                    <div style={{ marginTop: 10, fontSize: 11.5, color: "#8B93A1" }}>
                      ไฟล์ต้นฉบับ: {detail.sap_filename || "-"} / {detail.bank_filename || "-"}
                    </div>
                  )}
                </div>

                {/* Tabs */}
                <div style={{ display: "flex", gap: 8, marginTop: 18, flexWrap: "wrap" }}>
                  {TABS.map((t) => {
                    const active = tab === t.key;
                    const toneColor = { green: "#1F6650", red: "#B3261E", amber: "#8A5A00", navy: "#132A3A", purple: "#5B2FA6" }[t.tone];
                    const count = t.key === "daily" ? filteredDailySummary.length
                      : t.key === "manual" ? manualMatches.length
                      : (filteredPayload?.[t.key] || []).length;
                    return (
                      <button
                        key={t.key}
                        onClick={() => setTab(t.key)}
                        style={{
                          display: "flex", alignItems: "center", gap: 6, padding: "8px 14px", borderRadius: 999,
                          border: `1.5px solid ${active ? toneColor : "#E7EAEF"}`,
                          background: active ? toneColor : "#fff",
                          color: active ? "#fff" : "#63697A",
                          fontSize: 12.5, fontWeight: 600, cursor: "pointer",
                        }}
                      >
                        {t.label}
                        <span style={{
                          background: active ? "rgba(255,255,255,0.25)" : "#F1F2F4",
                          borderRadius: 999, padding: "1px 7px", fontFamily: "'IBM Plex Mono', monospace", fontSize: 11,
                        }}>
                          {count}
                        </span>
                      </button>
                    );
                  })}
                </div>

                {/* Date filter — narrows the tabs below only; summary above
                    always shows the full saved period */}
                <div
                  style={{
                    background: "#fff", borderRadius: 12, border: "1px solid #E7E3D8", padding: "12px 16px",
                    marginTop: 12, display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap",
                  }}
                >
                  <span style={{ fontSize: 12.5, color: "#63697A", fontWeight: 600 }}>กรองช่วงวันที่:</span>
                  <input
                    type="date"
                    value={dateFrom}
                    onChange={(e) => setDateFrom(e.target.value)}
                    style={{ fontSize: 12.5, padding: "7px 10px", borderRadius: 8, border: "1px solid #DADFE6", outline: "none", fontFamily: "inherit" }}
                  />
                  <span style={{ fontSize: 12.5, color: "#8B93A1" }}>ถึง</span>
                  <input
                    type="date"
                    value={dateTo}
                    onChange={(e) => setDateTo(e.target.value)}
                    style={{ fontSize: 12.5, padding: "7px 10px", borderRadius: 8, border: "1px solid #DADFE6", outline: "none", fontFamily: "inherit" }}
                  />
                  {hasDateFilter && (
                    <button
                      onClick={() => { setDateFrom(""); setDateTo(""); }}
                      style={{ fontSize: 12.5, color: "#B3261E", background: "transparent", border: "none", cursor: "pointer", fontWeight: 600 }}
                    >
                      ล้างตัวกรอง
                    </button>
                  )}
                  <span style={{ fontSize: 11.5, color: "#8B93A1", marginLeft: "auto" }}>
                    ดูวันที่เท่านั้น ไม่สนใจปี — ใช้กรองตารางด้านล่าง
                  </span>
                </div>

                <div style={{ background: "#fff", borderRadius: 16, border: "1px solid #E7E3D8", padding: 18, marginTop: 12 }}>
                  {(tab === "sapUnmatched" || tab === "bankUnmatched") && supportsManualMatch && (
                    <ManualMatchTray
                      selectedSapRows={selectedSapRows}
                      selectedBankRows={selectedBankRows}
                      onConfirm={confirmManualMatch}
                      onClear={clearSelection}
                    />
                  )}
                  {tab === "manual" ? (
                    !supportsManualMatch ? (
                      <div style={{ padding: 24, textAlign: "center", color: "#8B93A1", fontSize: 12.5 }}>
                        รายงานนี้บันทึกไว้ก่อนมีฟีเจอร์จับคู่ยอดต่าง — ไม่สามารถจับคู่เพิ่มเติมย้อนหลังได้
                      </div>
                    ) : (
                      <div>
                        <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap", marginBottom: 16 }}>
                          <button
                            onClick={runSuggestions}
                            disabled={suggesting}
                            style={{
                              display: "flex", alignItems: "center", gap: 6, padding: "9px 16px", borderRadius: 8, border: "none",
                              background: suggesting ? "#C8CCD4" : "#5B2FA6", color: "#fff", fontSize: 12.5, fontWeight: 600,
                              cursor: suggesting ? "not-allowed" : "pointer", whiteSpace: "nowrap",
                            }}
                          >
                            {suggesting ? (
                              <><Loader2 size={13} style={{ animation: "spin 1s linear infinite" }} /> กำลังวิเคราะห์...</>
                            ) : (
                              <>🔍 วิเคราะห์หาคู่ที่เป็นไปได้</>
                            )}
                          </button>
                          <span style={{ fontSize: 11.5, color: "#8B93A1" }}>
                            เทียบยอดเงิน + วันที่ของ SAP {editablePayload.sapUnmatched.length} รายการ กับ Bank {editablePayload.bankUnmatched.length} รายการที่เหลือ
                          </span>
                        </div>

                        {visibleSuggestions.length > 0 && (
                          <div style={{ marginBottom: 20 }}>
                            <div style={{ fontSize: 12, fontWeight: 700, color: "#5B2FA6", marginBottom: 8 }}>
                              ข้อเสนอแนะ ({visibleSuggestions.length}) — เรียงจากโอกาสตรงกันมากไปน้อย
                            </div>
                            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                              {visibleSuggestions.map((s) => (
                                <SuggestionCard key={s.id} suggestion={s} onAccept={() => acceptSuggestion(s)} onDismiss={() => dismissSuggestion(s.id)} />
                              ))}
                            </div>
                          </div>
                        )}
                        {suggestions !== null && visibleSuggestions.length === 0 && (
                          <div style={{ fontSize: 12.5, color: "#8B93A1", marginBottom: 20 }}>ไม่พบรายการที่น่าจะจับคู่กันได้จากเกณฑ์ที่ตั้งไว้</div>
                        )}

                        <div style={{ fontSize: 12, fontWeight: 700, color: "#132A3A", marginBottom: 8 }}>
                          ยืนยันแล้ว ({manualMatches.length})
                        </div>
                        <ManualMatchPanel groups={manualMatches} onUndo={undoManualMatch} />
                      </div>
                    )
                  ) : (
                    <DataTable
                      columns={TAB_COLUMNS[tab]}
                      rows={(tab === "daily" ? filteredDailySummary : filteredPayload?.[tab]) || []}
                      emptyText="ไม่มีรายการ"
                      exportFilename={`CSA_${detail.period}_${TABS.find((t) => t.key === tab)?.label || tab}.xlsx`}
                      {...(tab === "sapUnmatched" && supportsManualMatch
                        ? { selectKey: "sapIdx", selectedIds: selectedSap, onToggleRow: toggleSapSelect, onToggleAll: toggleAllSap }
                        : {})}
                      {...(tab === "bankUnmatched" && supportsManualMatch
                        ? { selectKey: "bankIdx", selectedIds: selectedBank, onToggleRow: toggleBankSelect, onToggleAll: toggleAllBank }
                        : {})}
                    />
                  )}
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
